import axios from 'axios';
import { ensureAccessToken, forceRefresh, goToPlatformLogin } from '../lib/platformAuth';

// 응답을 못 받는 요청을 언제까지 기다릴지.
// 기본값(0)은 '무한정'이라서, 서버가 늦어지면 그 요청이 영영 끝나지 않는다.
// 브라우저는 같은 서버에 HTTP/1.1 연결을 6개까지만 쓰므로, 걸린 요청 몇 개가
// 뒤에 줄 선 나머지를 전부 막아 화면이 멈춘 것처럼 보인다. 그래서 상한을 둔다.
const REQUEST_TIMEOUT_MS = 30000;

const api = axios.create({
  // 환경 변수 기반의 baseURL 설정 (Vite에서 .env 파일의 설정을 자동으로 가져옴)
  baseURL: import.meta.env.VITE_API_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

/*
 * 토큰을 붙인다.
 *
 * 예전에는 localStorage 에서 꺼냈다. 이제 토큰은 **메모리에만** 있고,
 * 없거나 만료가 가까우면 ensureAccessToken() 이 플랫폼에서 조용히 받아 온다.
 * 그래서 이 인터셉터가 async 다 — 토큰을 받아올 때까지 요청을 보류한다.
 */
api.interceptors.request.use(
  async (config) => {
    const token = await ensureAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 401 이 동시에 여러 건 떨어져도 로그인으로는 한 번만 보낸다.
let isRedirectingToLogin = false;

/*
 * 403 도 마찬가지다. 화면 하나가 뜰 때 메뉴·위젯이 저마다 요청을 보내므로,
 * 권한이 없으면 403 이 한 번에 여러 건 떨어진다. 막지 않으면 같은 경고창을
 * 메뉴 개수만큼 닫아야 한다.
 */
let isShowingForbidden = false;
let isLeavingForbidden = false;

/** 경고를 다시 띄울 수 있게 되기까지의 간격. 이동이 안 끝난 경우를 대비한 안전장치다. */
const FORBIDDEN_ALERT_GAP_MS = 3000;

// 응답 인터셉터 (401, 403 권한 에러 전역 처리)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      /*
       * 토큰이 만료됐거나 플랫폼이 거절했다.
       *
       * 예전에는 곧바로 /login 으로 보냈다. 이제는 **한 번 더 받아 본다** —
       * 액세스 토큰(15분)이 만료됐을 뿐 제품 쿠키는 살아 있는 경우가 대부분이라,
       * 조용히 갱신하면 사용자는 끊김을 느끼지 않는다.
       * 그래도 안 되면 그때 플랫폼 로그인으로 보낸다.
       *
       * 재시도는 요청당 한 번만 한다. 표시가 없으면 갱신 → 401 → 갱신 이 무한히 돈다.
       */
      const original = error.config;
      if (original && !original._retriedAfterRefresh) {
        original._retriedAfterRefresh = true;
        const renewed = await forceRefresh();
        if (renewed) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${renewed}`;
          return api.request(original);
        }
      }

      if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;
        goToPlatformLogin();
      }
      // 이동하는 동안 호출부가 오류 화면을 그리지 않도록 영원히 미결로 둔다.
      return new Promise(() => {});
    }

    if (error.response?.status === 403) {
      /*
       * 먼저, **로그인 단계의 403** 은 여기서 건드리지 않고 호출부로 넘긴다.
       *
       * 아래 처리는 "쓰던 중에 권한이 바뀌었다" 를 위한 것이다. 로그인 자체가
       * 거절된 경우까지 여기로 오면 alert 를 띄우고 /dashboard 로 보내는데,
       * 그 화면이 다시 로그인을 시도해 또 403 을 받는다 — alert 를 끝없이 누르는
       * 루프가 된다. (401 쪽에서 한 번 겪은 것과 같은 모양이다)
       *
       * 이런 403 은 AuthContext 가 받아 안내 화면(AccessDenied)으로 끝낸다.
       */
      const authLevelCodes = [
        'TENANT_MISSING',             // 기업 정보가 없는 토큰
        'PLATFORM_REJECTED',          // 플랫폼이 구독·권한을 근거로 거절
      ];
      const code = error.response.data?.code;
      const url = error.config?.url || '';

      if (authLevelCodes.includes(code) || url.includes('/auth/me')) {
        return Promise.reject(error);
      }

      /*
       * 라이선스 차단 화면은 없앴다.
       *
       * 설치형일 때 쓰던 license.lic 방식은 WORK&Hub 에서 쓰지 않는다. 구독·체험
       * 만료는 플랫폼이 판정해 /api/auth/me 의 access(readable·writable)로 알려
       * 주므로, 화면이 그 값으로 안내한다. 여기서 body 를 통째로 갈아끼우면
       * 플랫폼이 "구독 중" 이라고 한 사용자에게도 라이선스 안내가 뜬다.
       */
      const msg =
        error.response.data?.message ||
        error.response.data?.Message ||
        '해당 작업에 대한 권한이 없습니다.';

      /*
       * 안내는 **한 번만** 띄운다.
       *
       * 화면 하나가 뜰 때 메뉴·위젯이 저마다 요청을 보낸다. 권한이 없으면 그게
       * 전부 403 으로 돌아오고, 예전에는 그때마다 alert 가 떴다 — 사용자는
       * 같은 경고를 메뉴 개수만큼 닫아야 했다.
       *
       * 뒤이어 어차피 화면을 옮기므로(아래), 첫 번째 것만 보여 주면 충분하다.
       * 전역 플래그로 막고, 이동이 일어나지 않는 경우를 대비해 잠시 뒤 푼다.
       */
      if (!error._skipAlert && !isShowingForbidden) {
        isShowingForbidden = true;
        alert(msg);
        setTimeout(() => { isShowingForbidden = false; }, FORBIDDEN_ALERT_GAP_MS);
      }

      /*
       * 이동도 한 번만 한다. 403 이 여러 건이면 location 대입이 그 수만큼
       * 일어나 브라우저가 같은 주소를 거듭 읽는다.
       */
      if (!isLeavingForbidden) {
        isLeavingForbidden = true;
        if (window.location.pathname === '/dashboard') {
          window.location.reload();
        } else {
          window.location.href = '/dashboard';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
